import { DEFAULT_LANG } from 'app/config/config';
import resources from 'app/translations/resources';
import { isEdelvives } from './utils';
// import edelvivesResources from 'edelvives/translations/resources';

// Idioma por defecto de la app, alineado con el fallbackLng de i18next.
export const DEFAULT_LANGUAGE = DEFAULT_LANG;

// Sufijo de las claves de `resources` que contienen las traducciones propias de Edelvives.
const EDELVIVES_RESOURCES_SUFFIX = '_Edelvives Digital Plus';

/**
 * Bundles de traducción de un tenant para todos los idiomas disponibles de la app.
 *
 * Cada idioma trae la lista de objetos `{ namespace: { clave: texto } }` que se inyectan en
 * i18next. Un idioma sin bundle resoluble devuelve `[]`, nunca `undefined`.
 *
 * @param {{name?: string}} [tenant] tenant actual, para resolver los bundles propios del tenant
 * @param {string} [defaultLanguage] idioma resuelto, usado como fallback de los idiomas sin bundle
 * @returns {Record<string, Array<Record<string, Record<string, string>>>>} bundles por idioma
 */
export function importLanguageResources(tenant, defaultLanguage) {
  // En allLanguages tenemos todas las claves del archivo resources, que contiene los idiomas origen y los específicos de cada cliente
  // ej ['es', 'en', 'es_GotItByME', 'en_GotItByME'....]
  const allLanguages = Object.keys(resources);
  // En availableLanguages vamos a obtener todos los idiomas que pueden existir en la app
  const availableLanguages = getAvailableLanguages();
  let languageKeys = {};
  const tenantName = tenant?.name;

  availableLanguages.forEach((lang) => {
    // Si en allLanguages tenemos el idioma específico del tenant, lo añadimos, si no, añadimos el idioma origen
    if (isEdelvives()) {
      const key = lang + EDELVIVES_RESOURCES_SUFFIX;
      // Fallback: primero busca el idioma específico del tenant, luego el idioma base, y finalmente array vacío
      languageKeys[lang] = resources[key] || resources[lang] || [];
    } else if (allLanguages.includes(lang + '_' + tenantName)) {
      languageKeys[lang] = resources[lang + '_' + tenantName];
    } else if (defaultLanguage !== 'es' && allLanguages.includes(defaultLanguage)) {
      languageKeys[lang] = resources[defaultLanguage];
    } else {
      // Fallback a array vacío si no existe el idioma
      languageKeys[lang] = resources[lang] || [];
    }
  });

  return languageKeys;
}

// Idiomas que tienen bundle de traducción real (claves de resources sin el sufijo de tenant).
// Es la whitelist de idiomas con interfaz: cualquier otro id (p. ej. los hexadecimales que
// genera POST /langs para versiones de contenido) no puede pintarse en la UI porque caería
// al fallbackLng de i18next.
export function getAvailableLanguages() {
  return [...new Set(Object.keys(resources).map((lang) => lang.split('_')[0]))];
}

/**
 * Comprueba si un idioma tiene algún bundle de traducción resoluble para un tenant concreto,
 * con la misma prioridad que aplica `importLanguageResources`: bundle específico del tenant,
 * bundle base y, en los builds de Edelvives, el bundle de «Edelvives Digital Plus».
 *
 * Es tenant-aware a propósito: los bundles `ca`/`eu`/`gl`/`mx`/`vl` solo existen con el sufijo de
 * Edelvives, así que darlos por buenos en cualquier tenant dejaría la UI sin traducciones.
 *
 * @param {string} language id de idioma
 * @param {{name?: string}} [tenant] tenant actual
 * @returns {boolean} true si el idioma tiene bundle para este tenant
 */
function hasBundleForTenant(language, tenant) {
  if (!language) return false;
  if (resources[`${language}_${tenant?.name}`]) return true;
  if (resources[language]) return true;
  return isEdelvives() && !!resources[language + EDELVIVES_RESOURCES_SUFFIX];
}

/**
 * Idiomas de interfaz disponibles del tenant (respuesta de `GET /langs`) que además tienen
 * bundle de traducción para ese tenant. Es la lista contra la que se coerciona el idioma
 * del usuario.
 *
 * Devuelve `[]` mientras no hay respuesta, para que la coerción no decida con la lista vacía.
 *
 * @param {Array<string|{id: string}>} tenantLanguages idiomas del tenant desde el duck de langs
 * @param {{name?: string}} [tenant] tenant actual, para resolver los bundles propios del tenant
 * @returns {string[]} ids de idioma con bundle
 */
export function getInterfaceLanguageIds(tenantLanguages, tenant) {
  if (!Array.isArray(tenantLanguages)) return [];
  return tenantLanguages
    .map((language) => (typeof language === 'string' ? language : language?.id))
    .filter((id) => hasBundleForTenant(id, tenant) || hasBundleForTenant(getBaseLanguage(id), tenant));
}

// Idioma base de un id regional: 'es-MX' → 'es'. Misma regla que usa i18next para resolver
// la jerarquía de bundles, y que getLanguageWithBundle aplica al decidir si hay recursos.
// Sin ella, una fila de `langs` con id regional (p. ej. `es-MX`) quedaría fuera de la lista
// de idiomas disponibles y la coerción expulsaría al usuario a otro idioma (review PR #1095).
function getBaseLanguage(language) {
  return typeof language === 'string' ? language.split(/[-_]/)[0] : null;
}

/**
 * Garantiza que el idioma recibido tiene ALGÚN bundle con el que pintar la UI: si no lo tiene ni
 * él ni su idioma base (`es-MX` → `es`) cae al idioma del tenant y, en último caso, al idioma por
 * defecto. Evita que i18next resuelva por `fallbackLng` con un `lang_id` sin recursos (p. ej. los
 * ids hexadecimales de las versiones de contenido).
 *
 * No degrada el id regional: cuando la base tiene bundle se devuelve el idioma original, porque la
 * jerarquía `es-MX` → `es` la resuelve i18next namespace a namespace y degradarlo aquí borraría la
 * localización propia del regional (p. ej. los bundles `es-MX` de Edelvives MX).
 *
 * @param {string} language idioma resuelto
 * @param {string} [tenantLanguage] `tenant_lang_id`
 * @param {{name?: string}} [tenant] tenant actual, para resolver los bundles propios del tenant
 * @returns {string} idioma con bundle garantizado
 */
export function getLanguageWithBundle(language, tenantLanguage, tenant) {
  if (hasBundleForTenant(language, tenant)) return language;

  if (hasBundleForTenant(getBaseLanguage(language), tenant)) return language;

  if (hasBundleForTenant(tenantLanguage, tenant)) return tenantLanguage;
  return DEFAULT_LANGUAGE;
}
